import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, loadSession, usd } from '../lib/api';
import { useAccess } from '../lib/access';
import { ErrorNote, Icon, Modal, useLoad } from '../components/ui';

const vs = (now: number, then: number) => (then > 0 ? `${now >= then ? '▲' : '▼'} ${Math.abs(Math.round(((now - then) / then) * 100))}% vs last ${new Date().toLocaleDateString([], { weekday: 'long' })}` : 'No sales a week ago');

function Card({ title, children, wide }: { title: string; children: React.ReactNode; wide?: boolean }) {
  return <div className="panel" style={wide ? { gridColumn: '1 / -1' } : undefined}><div className="panel-head"><h2>{title}</h2></div><div className="panel-body">{children}</div></div>;
}

function Hourly({ rows }: { rows: { hour: number; today: number; lastWeek: number }[] }) {
  const open = rows.filter((r) => r.today || r.lastWeek);
  const first = Math.max(0, (open[0]?.hour ?? 8) - 1); const last = Math.min(23, (open[open.length - 1]?.hour ?? 20) + 1);
  const shown = rows.filter((r) => r.hour >= first && r.hour <= last);
  const max = Math.max(1, ...shown.flatMap((r) => [r.today, r.lastWeek]));
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 140 }}>
        {shown.map((r) => (
          <div key={r.hour} title={`${r.hour % 12 || 12}${r.hour < 12 ? 'am' : 'pm'}: ${usd(r.today)} today, ${usd(r.lastWeek)} last week`} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 1, height: '100%' }}>
            <div style={{ flex: 1, background: '#b9c7bf', height: `${(r.lastWeek / max) * 100}%`, borderRadius: 2 }} />
            <div style={{ flex: 1, background: 'var(--green-800, #28523c)', height: `${(r.today / max) * 100}%`, borderRadius: 2 }} />
          </div>))}
      </div>
      <div style={{ display: 'flex', gap: 3 }}>{shown.map((r) => <div key={r.hour} className="faint" style={{ flex: 1, textAlign: 'center', fontSize: 10 }}>{r.hour % 12 || 12}</div>)}</div>
      <div className="faint">Dark: today. Light: the same day last week.</div>
    </>
  );
}

function Trend({ rows }: { rows: { day: string; net: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.net));
  const pts = rows.map((r, i) => `${(i / (rows.length - 1)) * 300},${70 - (r.net / max) * 64}`).join(' ');
  return (
    <>
      <svg viewBox="0 0 300 76" role="img" aria-label="Net sales for the last 14 days" style={{ width: '100%', height: 110 }}>
        <polyline points={pts} fill="none" stroke="var(--green-800, #28523c)" strokeWidth="2" />
        {rows.map((r, i) => <circle key={r.day} cx={(i / (rows.length - 1)) * 300} cy={70 - (r.net / max) * 64} r="2.5" fill="var(--green-800, #28523c)"><title>{`${r.day}: ${usd(r.net)}`}</title></circle>)}
      </svg>
      <div className="faint">Total: {usd(rows.reduce((s, r) => s + r.net, 0))} · best day {usd(max)}</div>
    </>
  );
}

function WidgetBody({ k, d }: { k: string; d: any }) {
  switch (k) {
    case 'sales_today': return (
      <div className="stats">
        <div><div className="v">{usd(d.sales_today.net_cents)}</div><div className="l">Net sales today</div></div>
        <div><div className="v">{d.sales_today.receipts}</div><div className="l">Receipts</div></div>
        <div><div className="v">{usd(d.sales_today.avg_cents)}</div><div className="l">Average receipt</div></div>
        <div style={{ gridColumn: '1 / -1' }} className="faint">{vs(d.sales_today.net_cents, d.sales_today.last_week_net_cents)}</div>
      </div>);
    case 'hourly': return <Hourly rows={d.hourly} />;
    case 'trend': return <Trend rows={d.trend} />;
    case 'top_items': return d.top_items.length ? <table className="grid"><tbody>{d.top_items.map((t: any, i: number) => <tr key={i}><td>{t.brand} {t.name}</td><td className="n">{t.units}</td><td className="n">{usd(t.net_cents)}</td></tr>)}</tbody></table> : <div className="empty">No sales yet today.</div>;
    case 'payment_mix': { const total = d.payment_mix.reduce((s: number, t: any) => s + t.cents, 0); return d.payment_mix.length ? <table className="grid"><tbody>{d.payment_mix.map((t: any) => <tr key={t.method}><td>{t.method.replace('_', ' ')}</td><td className="n">{usd(t.cents)}</td><td className="n">{total ? Math.round((t.cents / total) * 100) : 0}%</td></tr>)}</tbody></table> : <div className="empty">No sales yet today.</div>; }
    case 'low_stock': return (<>
      <div className="stats"><div><div className="v">{d.low_stock.count}</div><div className="l">Items at or below reorder point</div></div></div>
      {d.low_stock.items.length > 0 && <table className="grid"><tbody>{d.low_stock.items.map((t: any, i: number) => <tr key={i}><td>{t.brand} {t.name}</td><td className="n">{t.on_hand} / {t.reorder_point}</td></tr>)}</tbody></table>}</>);
    case 'inventory_value': return (
      <div className="stats"><div><div className="v">{usd(d.inventory_value.cost_cents)}</div><div className="l">At your cost</div></div><div><div className="v">{usd(d.inventory_value.retail_cents)}</div><div className="l">At shelf price</div></div><div><div className="v">{d.inventory_value.units}</div><div className="l">Bottles</div></div></div>);
    case 'cash_drawers': return (
      <div className="stats"><div><div className="v">{d.cash_drawers.open}</div><div className="l">Drawers open</div></div><div><div className="v">{d.cash_drawers.total}</div><div className="l">Opened today</div></div>
        <div><div className="v" style={{ color: d.cash_drawers.variance_cents < 0 ? 'var(--bad)' : undefined }}>{usd(d.cash_drawers.variance_cents)}</div><div className="l">Over / short (closed)</div></div></div>);
    case 'lottery': return (
      <div className="stats"><div><div className="v">{usd(d.lottery.sales_cents)}</div><div className="l">Lottery sold today</div></div><div><div className="v">{d.lottery.open_issues}</div><div className="l">Open lottery issues</div></div></div>);
    case 'accounts_owed': return <div className="stats"><div><div className="v">{usd(d.accounts_owed.cents)}</div><div className="l">Owed by house account customers</div></div></div>;
    default: return null;
  }
}

const dayLabel = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });

// The latest AI reading of the business: yesterday's sales and orders, what to watch, and what to do. Owner only.
function InsightsCard() {
  const list = useLoad(() => api<any[]>('/api/briefs'), []);
  const id = list.data?.[0]?.id ?? null;
  const one = useLoad(() => (id ? api<any>(`/api/briefs/${id}`) : Promise.resolve(null)), [id]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const b = one.data?.brief;
  const bullets = (title: string, xs: string[], tone?: string) => (xs?.length ? <div><div className="faint" style={{ fontWeight: 700, marginTop: 8 }}>{title}</div><ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>{xs.slice(0, 4).map((x, i) => <li key={i} style={tone ? { color: tone } : undefined}>{x}</li>)}</ul></div> : null);
  return (
    <div className="panel" style={{ gridColumn: '1 / -1' }}>
      <div className="panel-head"><h2>AI Business Insights</h2><span className="grow" />
        <button disabled={busy} onClick={async () => { setBusy(true); setErr(null); try { await api('/api/briefs/generate', { body: {} }); list.reload(); one.reload(); } catch (e) { setErr(e); } finally { setBusy(false); } }}>{busy ? 'Working…' : 'Update now'}</button>
        <Link className="button" to="/morning">See all</Link></div>
      <div className="panel-body">
        <ErrorNote error={err ?? list.error ?? one.error} />
        {list.data && !b && <div className="empty">No insights yet. Press Update now to read yesterday, or wait for tomorrow morning.</div>}
        {b && (
          <div>
            <div className="faint">{dayLabel(one.data.for_date)} · {one.data.ai_used ? 'written by the AI helper' : 'plain summary'}</div>
            <h3 style={{ margin: '2px 0 4px' }}>{b.headline}</h3>
            <p style={{ margin: 0 }}>{b.summary}</p>
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
              {bullets('Highlights', b.highlights)}{bullets('Keep an eye on', b.watch, 'var(--bad)')}{bullets('To do', b.todo)}
            </div>
          </div>)}
      </div>
    </div>
  );
}

// The dashboard: each person picks the cards they want and the order. Saved on their login.
export default function Dashboard() {
  const dash = useLoad(() => api<{ data: any; config: { key: string; enabled: boolean }[]; widgets: { key: string; label: string }[] }>('/api/dashboard'), []);
  const [editing, setEditing] = useState(false);
  const d = dash.data;
  const { has } = useAccess();
  const showInsights = loadSession()?.user.role === 'owner' && has('daily_brief');
  const label = (k: string) => d?.widgets.find((w) => w.key === k)?.label ?? k;
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Dashboard</h1><p>How the store is doing right now.</p></div>
        <button onClick={() => dash.reload()}><Icon name="refresh" />Refresh</button>
        <button onClick={() => setEditing(true)}><Icon name="sliders" />Choose what to show</button>
      </div>
      <ErrorNote error={dash.error} />
      {d && (
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
          {showInsights && <InsightsCard />}
          {d.config.filter((w) => w.enabled).map((w) => <Card key={w.key} title={label(w.key)} wide={w.key === 'hourly' || w.key === 'trend'}><WidgetBody k={w.key} d={d.data} /></Card>)}
        </div>
      )}
      {d && d.config.every((w) => !w.enabled) && <div className="empty">Everything is hidden. Use “Choose what to show”.</div>}
      {editing && d && <Customize config={d.config} label={label} onClose={() => setEditing(false)} onDone={() => { setEditing(false); dash.reload(); }} />}
    </div>
  );
}

function Customize({ config, label, onClose, onDone }: { config: { key: string; enabled: boolean }[]; label: (k: string) => string; onClose: () => void; onDone: () => void }) {
  const [list, setList] = useState(config);
  const [err, setErr] = useState<unknown>(null);
  const move = (i: number, by: number) => setList((l) => { const n = [...l]; const j = i + by; if (j < 0 || j >= n.length) return l; [n[i], n[j]] = [n[j]!, n[i]!]; return n; });
  return (
    <Modal title="What the dashboard shows" onClose={onClose} footer={<button className="primary" onClick={async () => { try { await api('/api/dashboard/config', { method: 'PUT', body: { widgets: list } }); onDone(); } catch (e) { setErr(e); } }}>Save</button>}>
      <ErrorNote error={err} />
      {list.map((w, i) => (
        <div key={w.key} className="m-line">
          <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={w.enabled} onChange={(e) => setList(list.map((x, j) => (j === i ? { ...x, enabled: e.target.checked } : x)))} aria-label={`Show ${label(w.key)}`} />
          <div className="grow">{label(w.key)}</div>
          <button aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
          <button aria-label="Move down" disabled={i === list.length - 1} onClick={() => move(i, 1)}>↓</button>
        </div>))}
    </Modal>
  );
}
